import { Pipe, PipeTransform } from '@angular/core';
import {
  PlaceholderDerivableControl,
  resolveFixedPlaceholder,
  resolvePlaceholder,
} from '../placeholder.util';

/**
 * Thin wrappers over `placeholder.util.ts` so every sheet template honours the
 * per-control `ui.showPlaceholder` opt-out without each of the 24 sheet components
 * re-declaring a helper method. All logic — including the default-`true` rule —
 * lives in the util; these pipes only adapt it to a template binding, the same
 * split `FileSizePipe` uses over `file-size.util.ts`.
 *
 * There are two pipes because the templates were written against two different
 * precedence rules and swapping one for the other changes user-visible copy. Match
 * the pipe to the idiom the binding already used — the util's header has the table.
 *
 * Both are `pure` (the default): they read no signals and hold no state, so output
 * depends only on the control record and the argument.
 */

/**
 * Schema-first — replaces `[placeholder]="control.ui.placeholder || '<fallback>'"`.
 *
 * ```html
 * [placeholder]="control | placeholder"                       <!-- was: … || ''    -->
 * [placeholder]="control | placeholder: '0'"                  <!-- was: … || '0'   -->
 * [placeholder]="control | placeholder: 'Click to add content…'"
 * ```
 */
@Pipe({
  name: 'placeholder',
})
export class PlaceholderPipe implements PipeTransform {
  transform(
    control: PlaceholderDerivableControl | null | undefined,
    fallback = ''
  ): string {
    return resolvePlaceholder(control, fallback);
  }
}

/**
 * Template-owned — replaces a hard-coded `placeholder="…"` attribute. The argument
 * always wins; `ui.placeholder` is deliberately not consulted, because these sites
 * never read it and several controls carry different text there.
 *
 * ```html
 * [placeholder]="control | fixedPlaceholder: 'Please select'"
 * [placeholder]="control | fixedPlaceholder: 'Select a date'"
 * ```
 */
@Pipe({
  name: 'fixedPlaceholder',
})
export class FixedPlaceholderPipe implements PipeTransform {
  transform(
    control: PlaceholderDerivableControl | null | undefined,
    text: string
  ): string {
    return resolveFixedPlaceholder(control, text);
  }
}
